import { useEffect, useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/DashboardLayout';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import {
  LayoutDashboard,
  AlertCircle,
  Building2,
  FileUp,
  Activity,
  AlertTriangle,
  Calendar,
  ArrowRight,
  Loader2,
  Flame,
  Target,
} from 'lucide-react';
import { apiFetch } from '../lib/supabase';
import { useTimezone } from '../contexts/TimezoneContext';
import { projectAuditCount } from '../lib/projectAuditHistory';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// ─── helpers ────────────────────────────────────────────────────────────────

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function isWithinDays(iso, days) {
  if (!iso) return false;
  const ts = new Date(iso).getTime();
  if (Number.isNaN(ts)) return false;
  return Date.now() - ts <= days * 24 * 60 * 60 * 1000;
}

const STATUS_STYLE = {
  completed: 'bg-green-100 text-green-800 border-green-200',
  processing: 'bg-blue-100 text-blue-800 border-blue-200',
  pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  failed: 'bg-red-100 text-red-800 border-red-200',
};

const StatusPill = ({ status }) => {
  const cls = STATUS_STYLE[status] || 'bg-muted text-foreground border-border';
  return (
    <span
      data-testid={`status-pill-${status || 'unknown'}`}
      className={`inline-block text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${cls}`}
    >
      {status || 'unknown'}
    </span>
  );
};

// ─── KPI tile ────────────────────────────────────────────────────────────────

const KPI_ACCENTS = {
  blue: {
    card: 'bg-black border-blue-400',
    iconWrap: 'bg-blue-500/15 text-blue-300',
    label: 'text-blue-300',
    hint: 'text-blue-200/60',
  },
  emerald: {
    card: 'bg-black border-emerald-400',
    iconWrap: 'bg-emerald-500/15 text-emerald-300',
    label: 'text-emerald-300',
    hint: 'text-emerald-200/60',
  },
  violet: {
    card: 'bg-black border-violet-400',
    iconWrap: 'bg-violet-500/15 text-violet-300',
    label: 'text-violet-300',
    hint: 'text-violet-200/60',
  },
  amber: {
    card: 'bg-black border-amber-400',
    iconWrap: 'bg-amber-500/15 text-amber-300',
    label: 'text-amber-300',
    hint: 'text-amber-200/60',
  },
};

const KpiTile = ({ icon: Icon, label, value, hint, testid, accent = 'blue', onDoubleClick, navPath }) => {
  const a = KPI_ACCENTS[accent] || KPI_ACCENTS.blue;
  return (
    <Card
      data-testid={testid}
      onDoubleClick={onDoubleClick}
      title={navPath ? 'Double-click to open' : undefined}
      className={`flex flex-col border-2 shadow-md hover:shadow-lg transition-shadow ${a.card} ${onDoubleClick ? 'cursor-pointer select-none' : ''}`}
    >
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-2">
          <p className={`text-xs font-bold uppercase tracking-wider ${a.label}`}>
            {label}
          </p>
          <span className={`p-1.5 rounded-md ${a.iconWrap}`}>
            <Icon className="h-4 w-4" strokeWidth={2.75} />
          </span>
        </div>
        <p className="text-3xl font-bold text-white tabular-nums">
          {value}
        </p>
        {hint && (
          <p className={`mt-1 text-xs ${a.hint}`}>{hint}</p>
        )}
      </CardContent>
    </Card>
  );
};

// ─── Needs attention next (cross-project priority queue) ────────────────────
//
// Renders the top-N ``/api/projects/portfolio/needs-attention`` items.
// Server returns audits already sorted by priority_score so the UI stays
// dumb — no client-side ranking. Each row deep-links to that audit's
// results page so the user can act on the recommendation in one click.
//
// Visual rules:
//   • Critical-failure rows are flagged with a red Flame icon.
//   • In-flight rows (queued / processing) get a blue Activity icon and
//     deep-link to the live progress page rather than results.
//   • Empty state ("everything's calm") is its own friendly card — it's
//     a feature signal, not an error.
function NeedsAttentionPanel({ items, available }) {
  if (!available) {
    // Decorative feature — quietly hide on backend failure rather than
    // blocking the whole dashboard. (Treat it the same way as we treat
    // HITL counts when Supabase is sleepy.)
    return null;
  }
  return (
    <Card className="mb-6 border-accent/40" data-testid="needs-attention-panel">
      <CardHeader className="bg-[hsl(var(--primary))] rounded-t-xl">
        <CardTitle className="flex items-center gap-2 text-white">
          <Target className="h-5 w-5 text-accent" />
          Needs attention next
        </CardTitle>
        <CardDescription className="text-white/70">
          Top audits across your portfolio, ranked by failure severity, HITL
          backlog, and age. Click a row to dive in.
        </CardDescription>
      </CardHeader>
      <CardContent className="p-0">
        {items.length === 0 ? (
          <div
            className="px-6 py-8 text-center text-sm text-muted-foreground"
            data-testid="needs-attention-empty"
          >
            <p className="mb-1 text-foreground font-medium">
              All clear — nothing flagged.
            </p>
            <p>
              Your portfolio has no audits with critical or major failures
              right now. Keep it up.
            </p>
          </div>
        ) : (
          <ul
            className="divide-y divide-border max-h-[272px] overflow-y-auto"
            data-testid="needs-attention-list"
          >
            {items.map((item) => (
              <NeedsAttentionRow key={item.audit_id} item={item} />
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function NeedsAttentionRow({ item }) {
  const isInFlight = item.status === 'processing' || item.status === 'queued';
  const isCritical = (item.critical_failures || 0) > 0;
  const RowIcon = isInFlight ? Activity : isCritical ? Flame : AlertTriangle;
  const iconColor = isInFlight
    ? 'text-blue-500'
    : isCritical
    ? 'text-red-500'
    : 'text-amber-500';
  const target = isInFlight
    ? `/audits/${item.audit_id}/progress`
    : `/results/${item.audit_id}`;

  return (
    <li
      className="px-6 py-3 hover:bg-muted/40 transition-colors"
      data-testid={`needs-attention-row-${item.audit_id}`}
    >
      <Link to={target} className="flex items-center gap-4 group">
        <RowIcon className={`h-5 w-5 flex-shrink-0 ${iconColor}`} />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-foreground truncate group-hover:text-primary transition-colors">
            {item.ifc_file_name || `audit ${item.audit_id.slice(0, 8)}`}
          </p>
          <p className="text-xs text-muted-foreground truncate">
            {item.project_name || item.project_code || 'Unknown project'} ·{' '}
            <span className="text-foreground/70">{item.reason}</span>
          </p>
        </div>
        <div className="flex items-center gap-4 flex-shrink-0">
          <NeedsAttentionBadges item={item} />
          <ArrowRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
      </Link>
    </li>
  );
}

function NeedsAttentionBadges({ item }) {
  // Show at most the two most informative chips so the row stays readable.
  const chips = [];
  if (item.critical_failures > 0) {
    chips.push({
      key: 'critical',
      label: `${item.critical_failures} critical`,
      cls: 'bg-red-100 text-red-800 border-red-200',
    });
  }
  if (item.major_failures > 0 && chips.length < 2) {
    chips.push({
      key: 'major',
      label: `${item.major_failures} major`,
      cls: 'bg-amber-100 text-amber-800 border-amber-200',
    });
  }
  if (item.hitl_pending > 0 && chips.length < 2) {
    chips.push({
      key: 'hitl',
      label: `${item.hitl_pending} HITL`,
      cls: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    });
  }
  if (chips.length === 0 && (item.warn_count || 0) > 0) {
    chips.push({
      key: 'warn',
      label: `${item.warn_count} warn`,
      cls: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    });
  }
  return (
    <div className="hidden sm:flex items-center gap-2">
      {chips.map((c) => (
        <span
          key={c.key}
          className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${c.cls}`}
          data-testid={`needs-attention-chip-${c.key}`}
        >
          {c.label}
        </span>
      ))}
    </div>
  );
}

// ─── page ────────────────────────────────────────────────────────────────────

export default function PortfolioDashboardPage() {
  const navigate = useNavigate();
  const { formatDateTime } = useTimezone();
  const [projects, setProjects] = useState([]);
  const [audits, setAudits] = useState([]);
  const [hitlByProject, setHitlByProject] = useState({}); // { [project_id]: number }
  const [hitlAvailable, setHitlAvailable] = useState(false);
  const [needsAttention, setNeedsAttention] = useState([]);
  const [needsAttentionAvailable, setNeedsAttentionAvailable] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        // 1. Projects + audits + needs-attention list in parallel.
        // The 'Needs attention next' panel is server-ranked (composite
        // priority score) so we don't replicate the math client-side.
        const [pResp, aResp, naResp] = await Promise.all([
          apiFetch(`${API_BASE}/api/projects`),
          apiFetch(`${API_BASE}/api/audits`),
          apiFetch(`${API_BASE}/api/projects/portfolio/needs-attention?limit=8`),
        ]);

        if (!pResp.ok) throw new Error(`Projects list failed: HTTP ${pResp.status}`);
        if (!aResp.ok) throw new Error(`Audits list failed: HTTP ${aResp.status}`);

        const pJson = await pResp.json();
        const aJson = await aResp.json();
        const ownedProjects = pJson.projects || [];
        const allAudits = aJson.audits || [];

        if (cancelled) return;
        setProjects(ownedProjects);
        setAudits(allAudits);

        // Needs-attention is decorative — render zero-state if it 500s
        // rather than blocking the whole dashboard.
        if (naResp.ok) {
          try {
            const naJson = await naResp.json();
            setNeedsAttention(Array.isArray(naJson.items) ? naJson.items : []);
            setNeedsAttentionAvailable(true);
          } catch {
            setNeedsAttentionAvailable(false);
          }
        } else {
          setNeedsAttentionAvailable(false);
        }

        // 2. HITL counts per project — fire all in parallel.
        // We only need the `total` field, so limit=1.
        const hitlResults = await Promise.allSettled(
          ownedProjects.map(async (p) => {
            const r = await apiFetch(
              `${API_BASE}/api/audits/hitl/queue?project_id=${encodeURIComponent(
                p.id
              )}&limit=1`
            );
            if (!r.ok) throw new Error(`HITL HTTP ${r.status}`);
            const j = await r.json();
            return { id: p.id, total: Number(j.total || 0) };
          })
        );

        if (cancelled) return;

        const map = {};
        let anyOk = false;
        hitlResults.forEach((res) => {
          if (res.status === 'fulfilled') {
            map[res.value.id] = res.value.total;
            anyOk = true;
          }
        });
        setHitlByProject(map);
        setHitlAvailable(anyOk || ownedProjects.length === 0);
        setError(null);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load dashboard');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  // ─── derived stats — REAL data only ────────────────────────────────────────

  const projectMap = useMemo(() => {
    const m = {};
    projects.forEach((p) => {
      m[p.id] = p;
    });
    return m;
  }, [projects]);

  const inProgressAudits = useMemo(
    () => audits.filter((a) => a.status === 'pending' || a.status === 'processing'),
    [audits]
  );
  const completedAudits = useMemo(
    () => audits.filter((a) => a.status === 'completed'),
    [audits]
  );
  const auditsThisWeek = useMemo(
    () => audits.filter((a) => isWithinDays(a.created_at, 7)),
    [audits]
  );

  const totalHitl = useMemo(
    () => Object.values(hitlByProject).reduce((s, n) => s + n, 0),
    [hitlByProject]
  );

  const recentAudits = useMemo(
    () =>
      [...audits]
        .sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''))
        .slice(0, 8),
    [audits]
  );

  // Per-project rollup comes directly from the uncapped database-row
  // aggregation attached to each `/api/projects` row.
  const projectRollup = useMemo(() => {
    return projects.map((p) => {
      return {
        project: p,
        audit_count: projectAuditCount(p),
        completed: p.completed ?? null,
        in_progress: p.in_progress ?? null,
        last_audit: p.last_audit || null,
        hitl_pending: hitlByProject[p.id] ?? null,
      };
    });
  }, [projects, hitlByProject]);

  // ─── render ────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <DashboardLayout>
        <div
          className="max-w-6xl mx-auto py-12 flex items-center justify-center text-muted-foreground"
          data-testid="dashboard-loading"
        >
          <Loader2 className="h-5 w-5 animate-spin mr-2" />
          Loading your portfolio…
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto" data-testid="portfolio-dashboard-page">
        {/* Header */}
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-foreground mb-2 flex items-center gap-2">
              <LayoutDashboard className="h-7 w-7 text-primary" /> Portfolio
              Dashboard
            </h1>
            <p className="text-muted-foreground">
              Real-time view of your projects, audits, and pending HITL items.
            </p>
          </div>
          <Button
            onClick={() => navigate('/dashboard')}
            data-testid="dashboard-new-audit-btn"
          >
            <FileUp className="h-4 w-4 mr-2" /> New Audit
          </Button>
        </div>

        {error && (
          <Alert variant="destructive" className="mb-6" data-testid="dashboard-error">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Empty state for brand-new users */}
        {projects.length === 0 && (
          <Card data-testid="dashboard-empty-projects">
            <CardContent className="py-10 text-center text-muted-foreground">
              You don't own any projects yet. Create one from{' '}
              <Link to="/projects" className="text-primary underline">
                Projects
              </Link>{' '}
              to get started.
            </CardContent>
          </Card>
        )}

        {projects.length > 0 && (
          <>
            {/* — KPI row — */}
            <div
              className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 mb-6"
              data-testid="dashboard-kpi-row"
            >
              <KpiTile
                testid="kpi-projects"
                icon={Building2}
                label="Projects"
                value={projects.length}
                hint="Owned by you"
                accent="blue"
                navPath="/projects"
                onDoubleClick={() => navigate('/projects')}
              />
              <KpiTile
                testid="kpi-audits-total"
                icon={FileUp}
                label="Audits (total)"
                value={audits.length}
                hint={`${completedAudits.length} completed`}
                accent="emerald"
                navPath="/history"
                onDoubleClick={() => navigate('/history')}
              />
              <KpiTile
                testid="kpi-audits-inprogress"
                icon={Activity}
                label="In progress"
                value={inProgressAudits.length}
                hint={
                  inProgressAudits.length > 0
                    ? 'Pending or processing'
                    : 'Nothing running right now'
                }
                accent="violet"
                navPath="/history"
                onDoubleClick={() => navigate('/history')}
              />
              <KpiTile
                testid="kpi-hitl-pending"
                icon={AlertTriangle}
                label="HITL items"
                value={hitlAvailable ? totalHitl : '—'}
                hint={
                  hitlAvailable
                    ? totalHitl === 0
                      ? 'Nothing waiting for review'
                      : 'Waiting for human review'
                    : 'Could not load HITL counts'
                }
                accent="amber"
                navPath="/hitl"
                onDoubleClick={() => navigate('/hitl')}
              />
            </div>

            {/* — This-week strip — */}
            <Card className="mb-6" data-testid="dashboard-week-strip">
              <CardContent className="p-4 flex items-center gap-3">
                <Calendar className="h-5 w-5 text-muted-foreground" />
                <div className="flex-1">
                  <p className="text-sm font-medium">
                    {auditsThisWeek.length === 0
                      ? 'No audits started in the last 7 days.'
                      : `${auditsThisWeek.length} audit${
                          auditsThisWeek.length === 1 ? '' : 's'
                        } started in the last 7 days.`}
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* — Needs attention next (cross-project priority queue) — */}
            <NeedsAttentionPanel
              items={needsAttention}
              available={needsAttentionAvailable}
            />

            <div className="grid gap-6 lg:grid-cols-2">
              {/* — Per-project rollup — */}
              <Card
                data-testid="dashboard-project-rollup"
              >
                <CardHeader className="bg-[hsl(var(--primary))] rounded-t-xl">
                  <CardTitle className="text-white">Projects</CardTitle>
                  <CardDescription className="text-white/70">
                    Live counts per project. Click a row to open it.
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-0">
                  <ul
                    className="divide-y divide-border max-h-[288px] overflow-y-auto"
                    data-testid="dashboard-project-rollup-list"
                  >
                    {projectRollup.map(({ project, audit_count, completed, in_progress, last_audit, hitl_pending }) => (
                      <li
                        key={project.id}
                        className="px-4 py-3 hover:bg-muted/40 transition-colors"
                        data-testid={`dashboard-project-row-${project.id}`}
                      >
                        <Link
                          to={`/projects/${project.id}`}
                          className="flex items-start justify-between gap-4"
                        >
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-foreground truncate">
                              {project.name}
                            </p>
                            <p className="text-xs text-muted-foreground mt-0.5">
                              {project.code || 'no code'} · last audit:{' '}
                              {last_audit ? formatDateTime(last_audit) : 'never'}
                            </p>
                          </div>
                          <div className="flex flex-col items-end gap-1 text-[11px] text-muted-foreground tabular-nums">
                            <span>
                              <strong className="text-foreground">
                                {audit_count == null ? '—' : audit_count}
                              </strong>{' '}
                              audits
                            </span>
                            <span>
                              {completed == null ? '—' : completed} completed · {in_progress == null ? '—' : in_progress} in progress
                            </span>
                            {hitl_pending !== null && hitl_pending > 0 && (
                              <span className="inline-block px-2 py-0.5 rounded border bg-yellow-100 text-yellow-800 border-yellow-200">
                                {hitl_pending} HITL
                              </span>
                            )}
                          </div>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

              {/* — Recent audits — */}
              <Card data-testid="dashboard-recent-audits">
                <CardHeader className="bg-[hsl(var(--primary))] rounded-t-xl">
                  <CardTitle className="text-white">Recent audits</CardTitle>
                  <CardDescription className="text-white/70">Newest first.</CardDescription>
                </CardHeader>
                <CardContent className="p-0">
                  {recentAudits.length === 0 ? (
                    <p
                      className="text-center text-muted-foreground py-6 text-sm"
                      data-testid="dashboard-no-audits"
                    >
                      No audits yet.
                    </p>
                  ) : (
                    <ul
                      className="divide-y divide-border max-h-[336px] overflow-y-auto"
                      data-testid="dashboard-recent-audits-list"
                    >
                      {recentAudits.map((a) => {
                        const proj = projectMap[a.project_id];
                        return (
                          <li
                            key={a.id}
                            className="px-4 py-3"
                            data-testid={`dashboard-audit-row-${a.id}`}
                          >
                            <Link
                              to={`/results/${a.id}`}
                              className="block group"
                            >
                              <div className="flex items-start justify-between gap-3">
                                <div className="flex-1 min-w-0">
                                  <p className="text-xs font-medium text-foreground truncate group-hover:text-primary transition-colors">
                                    {a.ifc_file_name || `audit ${a.id.slice(0, 8)}`}
                                  </p>
                                  <p className="text-[11px] text-muted-foreground mt-0.5 truncate">
                                    {proj ? proj.name : 'Unknown project'}
                                  </p>
                                  <p className="text-[10px] text-muted-foreground/70 mt-0.5">
                                    {formatDateTime(a.created_at)}
                                  </p>
                                </div>
                                <div className="flex flex-col items-end gap-1">
                                  <StatusPill status={a.status} />
                                  <ArrowRight className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                                </div>
                              </div>
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Footnote: honesty about what is and isn't computed here */}
            <p
              className="text-[11px] text-muted-foreground/70 mt-6"
              data-testid="dashboard-honesty-note"
            >
              All numbers above are live counts from your projects and audits.
              The "Needs attention next" list is server-ranked by failure
              severity (critical × 10 + major × 4 + minor × 1) plus HITL
              backlog and audit age — no AI guesswork. Cross-audit confidence
              trend charts are still on the backlog.
            </p>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
